import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import { useFeature } from '@modules/root';
import { SubscribePublicationsTrigger } from '@modules-rocco/subscription/component/SubscribePublicationsTrigger';
import promoSceneImage from '../images/promo-band-scene.jpg';

interface MagazinePromoBandProps {
    headline: string;
    body?: string;
    /** Where Subscribe Now leads when the publications chooser is off. */
    subscribeLink: string;
    /** Hidden for signed-in members, who already have an account. */
    showCreateAccount?: boolean;
    createAccountLink?: string;
    className?: string;
}

/**
 * The digital magazine promo band on /articles.
 *
 * Figma 60:2173: a 1920x956 #f5f5f5 band with a magazine still life at the left
 * and the subscribe pitch opposite. The headline is white under
 * mix-blend-difference in the design, which reads near-black on this ground and
 * inverts cleanly if the ground ever changes.
 *
 * The left side is the frame's own artwork, exported and bundled, not the
 * latest issue's cover: it is a lit interior with the magazine composed into
 * it, and a bare CMS cover dropped into that slot has neither the setting nor
 * the crop the composition is built around.
 */
export const MagazinePromoBand = ({
    headline,
    body,
    subscribeLink,
    showCreateAccount = true,
    createAccountLink = '/register',
    className,
}: MagazinePromoBandProps) => {
    const canChoosePublications = useFeature(
        'digital_publication_subscribe_2026',
    );

    return (
        <section
            aria-label="Subscribe to our digital publications"
            className={cn(
                'relative w-full bg-[#f5f5f5]',
                'aspect-[1920/956] max-md:aspect-auto max-md:py-[2.5rem]',
                className,
            )}
        >
            {/* Desktop: the Figma composition against the frame. */}
            <div className="absolute inset-0 max-md:hidden">
                <img
                    src={promoSceneImage}
                    alt=""
                    className="absolute object-cover"
                    style={{
                        left: '7.554%',
                        top: '8.164%',
                        width: '30.895%',
                        height: '83.682%',
                    }}
                />

                <p
                    className="absolute m-0 font-ghost font-twk-ghost text-[3.375rem] leading-[1.1] font-normal tracking-[-0.1013rem] text-white mix-blend-difference"
                    style={{
                        left: '53.86%',
                        top: '21.826%',
                        width: '34.13%',
                    }}
                >
                    {headline}
                </p>

                {body && (
                    <p
                        className="absolute m-0 line-clamp-3 font-sans text-[1.375rem] leading-[1.25] font-medium tracking-[-0.0344rem] text-[#181818]"
                        style={{
                            left: '53.86%',
                            top: '56.563%',
                            width: '32.364%',
                        }}
                    >
                        {body}
                    </p>
                )}

                <div
                    className="absolute flex items-center gap-[0.75rem]"
                    style={{ left: '53.86%', top: '68.531%' }}
                >
                    {canChoosePublications ? (
                        <SubscribePublicationsTrigger
                            label="Subscribe Now"
                            className="h-[3.25rem] px-[1.723rem] text-[1rem]"
                        />
                    ) : (
                        <RoccoLink
                            to={subscribeLink}
                            className="flex h-[3.25rem] items-center justify-center rounded-full bg-[#181818] px-[1.723rem] font-sans text-[1rem] leading-[1.3] font-medium text-[#f3f3f3] drop-shadow-[0_0.2027rem_0.7094rem_rgba(0,0,0,0.11)] transition-colors hover:bg-[#3d3d3d]"
                        >
                            Subscribe Now
                        </RoccoLink>
                    )}
                    {showCreateAccount && (
                        <RoccoLink
                            to={createAccountLink}
                            className="flex h-[3.25rem] items-center justify-center rounded-full border border-solid border-[#181818] px-[1.723rem] font-sans text-[1rem] leading-[1.3] font-medium text-[#181818] shadow-[0_0.2027rem_1.4189rem_rgba(0,0,0,0.11)] transition-colors hover:bg-black/5"
                        >
                            Create Account
                        </RoccoLink>
                    )}
                </div>
            </div>

            {/* Mobile: cover above the pitch. */}
            <div className="flex flex-col items-start gap-[1.25rem] px-[1.5rem] md:hidden">
                <img
                    src={promoSceneImage}
                    alt=""
                    className="aspect-[593/800] w-[80%] max-w-[20rem] self-center object-cover"
                />
                <p className="m-0 font-ghost font-twk-ghost text-[1.75rem] leading-[1.1] font-normal tracking-[-0.05rem] text-[#181818]">
                    {headline}
                </p>
                {body && (
                    <p className="m-0 line-clamp-4 font-sans text-[1rem] leading-[1.25] font-medium text-[#181818]">
                        {body}
                    </p>
                )}
                <div className="flex flex-wrap items-center gap-[0.75rem]">
                    {canChoosePublications ? (
                        <SubscribePublicationsTrigger
                            label="Subscribe Now"
                            className="h-[2.75rem] px-[1.5rem] text-[0.875rem]"
                        />
                    ) : (
                        <RoccoLink
                            to={subscribeLink}
                            className="flex h-[2.75rem] items-center justify-center rounded-full bg-[#181818] px-[1.5rem] font-sans text-[0.875rem] font-medium text-[#f3f3f3]"
                        >
                            Subscribe Now
                        </RoccoLink>
                    )}
                    {showCreateAccount && (
                        <RoccoLink
                            to={createAccountLink}
                            className="flex h-[2.75rem] items-center justify-center rounded-full border border-solid border-[#181818] px-[1.5rem] font-sans text-[0.875rem] font-medium text-[#181818]"
                        >
                            Create Account
                        </RoccoLink>
                    )}
                </div>
            </div>
        </section>
    );
};
